// ProWelcome.js
import { useEffect, useState } from "react";
import SummitSubmark from "./Summit-submark-white.png";

const PRO_FEATURES = [
  { icon: "∞", label: "Unlimited messages", sub: "No daily limits, ever" },
  { icon: "📋", label: "Past paper mode", sub: "Mark scheme + model A-grade answers" },
  { icon: "⚡", label: "Drill mode", sub: "Targeted questions on your weak spots" },
  { icon: "📝", label: "Mock exam mode", sub: "Full SQA-style papers, marked instantly" },
  { icon: "📉", label: "Weakness tracker", sub: "Automatically tracks where you drop marks" },
  { icon: "📅", label: "Revision planner", sub: "Day-by-day schedule built around your exams" },
];

const ELITE_FEATURES = [
  { icon: "∞", label: "Unlimited messages", sub: "No daily limits, ever" },
  { icon: "📋", label: "Past paper mode", sub: "Mark scheme + model A-grade answers" },
  { icon: "⚡", label: "Drill mode", sub: "Targeted questions on your weak spots" },
  { icon: "📝", label: "Mock exam mode", sub: "Full SQA-style papers, marked instantly" },
  { icon: "🎯", label: "All predicted papers free", sub: "Every subject, every paper — included" },
  { icon: "🤖", label: "AI study coach", sub: "Knows your subjects, tracks your progress" },
];

// Google Ads purchase conversion tracking
function trackPurchaseConversion(plan) {
  if (typeof window.gtag === "function") {
    window.gtag("event", "conversion", {
      send_to: "AW-18142220512/wuoBCJuv_qccEOCh8cpD",
      value: plan === "elite" ? 14.99 : 6.99,
      currency: "GBP",
    });
  }
}

export default function ProWelcome({ onContinue, examDate, plan = "pro" }) {
  const [visible, setVisible] = useState(false);
  const [featuresVisible, setFeaturesVisible] = useState(false);

  const daysUntil = examDate
    ? Math.max(0, Math.ceil((new Date(examDate) - new Date()) / (1000 * 60 * 60 * 24)))
    : null;

  useEffect(() => {
    setTimeout(() => setVisible(true), 100);
    setTimeout(() => setFeaturesVisible(true), 600);
    // ✅ Fire Google Ads purchase conversion on page load
    trackPurchaseConversion(plan);
  }, [plan]);

  return (
    <div style={{ minHeight: "100vh", background: "#070d0c", color: "#f0f4f2", fontFamily: "'DM Sans', sans-serif", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "40px 24px", position: "relative", overflow: "hidden" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Instrument+Serif:ital,wght@0,400;1,400&family=DM+Sans:wght@300;400;500;600&display=swap');

        @keyframes fadeUp { from{opacity:0;transform:translateY(24px)} to{opacity:1;transform:translateY(0)} }
        @keyframes scaleIn { from{opacity:0;transform:scale(0.8)} to{opacity:1;transform:scale(1)} }
        @keyframes glow { 0%,100%{opacity:0.5} 50%{opacity:1} }
        @keyframes rotate { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
        @keyframes shimmer { 0%{background-position:-200% center} 100%{background-position:200% center} }
        @keyframes pulse { 0%,100%{transform:scale(1)} 50%{transform:scale(1.05)} }
        @keyframes float { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-6px)} }
        @keyframes particleFade { 0%{opacity:0;transform:translateY(0) scale(0)} 20%{opacity:1} 100%{opacity:0;transform:translateY(-120px) scale(1.5)} }

        .pro-badge {
          background: linear-gradient(135deg, #1D9E75, #4DC9A0, #1D9E75);
          background-size: 200% auto;
          animation: shimmer 3s linear infinite;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .feature-item {
          opacity: 0;
          transform: translateY(16px);
          transition: opacity 0.5s ease, transform 0.5s ease;
        }
        .feature-item.visible {
          opacity: 1;
          transform: translateY(0);
        }

        .hero-icon {
          animation: float 3s ease-in-out infinite;
        }

        .continue-btn {
          background: #1D9E75;
          color: white;
          border: none;
          padding: 16px 48px;
          border-radius: 12px;
          font-size: 16px;
          font-family: 'DM Sans', sans-serif;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          letter-spacing: -0.2px;
        }
        .continue-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 32px rgba(29,158,117,0.4);
        }
        .continue-btn:active { transform: translateY(0); }

        .particle {
          position: absolute;
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: #1D9E75;
          animation: particleFade 2.5s ease-out forwards;
          pointer-events: none;
        }
      `}</style>

      {/* Background glow */}
      <div style={{ position: "absolute", inset: 0, background: "radial-gradient(ellipse 60% 50% at 50% 30%, rgba(29,158,117,0.12) 0%, transparent 70%)", pointerEvents: "none" }} />

      {/* Floating particles */}
      {[...Array(12)].map((_, i) => (
        <div key={i} className="particle" style={{
          left: `${15 + (i * 7) % 70}%`,
          top: `${30 + (i * 13) % 40}%`,
          animationDelay: `${i * 0.2}s`,
          opacity: 0,
          background: i % 3 === 0 ? "#1D9E75" : i % 3 === 1 ? "#4DC9A0" : "#EF9F27",
          width: `${4 + (i % 3) * 2}px`,
          height: `${4 + (i % 3) * 2}px`,
        }} />
      ))}

      <div style={{ maxWidth: 600, width: "100%", textAlign: "center", position: "relative", zIndex: 1 }}>

        {/* Logo */}
        <div style={{ opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(20px)", transition: "all 0.6s cubic-bezier(0.16,1,0.3,1)", marginBottom: 32 }}>
          <img src={SummitSubmark} alt="Summit" style={{ height: 150, width: "auto", display: "block", margin: "0 auto" }} />
        </div>

        {/* Main icon */}
        <div className="hero-icon" style={{ opacity: visible ? 1 : 0, transform: visible ? "scale(1)" : "scale(0.5)", transition: "all 0.7s cubic-bezier(0.34,1.56,0.64,1) 0.1s", marginBottom: 28 }}>
          <div style={{ width: 88, height: 88, borderRadius: 24, background: "rgba(29,158,117,0.12)", border: "1px solid rgba(29,158,117,0.3)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto", boxShadow: "0 0 40px rgba(29,158,117,0.2)" }}>
            <div style={{ fontFamily: "'Instrument Serif', serif", fontSize: 42, lineHeight: 1 }}>✦</div>
          </div>
        </div>

        {/* Headline */}
        <div style={{ opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(20px)", transition: "all 0.6s cubic-bezier(0.16,1,0.3,1) 0.2s", marginBottom: 12 }}>
          <div style={{ fontFamily: "'Instrument Serif', serif", fontSize: "clamp(36px, 6vw, 52px)", letterSpacing: -1.5, lineHeight: 1.1, color: "#f0f4f2" }}>
            Welcome to<br />
            <em className="pro-badge" style={{ fontStyle: "italic" }}>{plan === "elite" ? "Summit Elite." : "Summit Pro."}</em>
          </div>
        </div>

        {/* Subheading */}
        <div style={{ opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(20px)", transition: "all 0.6s cubic-bezier(0.16,1,0.3,1) 0.3s", marginBottom: 16 }}>
          <p style={{ fontSize: 17, color: "rgba(240,244,242,0.45)", fontWeight: 300, lineHeight: 1.7, maxWidth: 440, margin: "0 auto" }}>
            {plan === "elite" ? "Your personal AI study coach is ready. Everything unlocked." : "Everything you need to reach your grade — unlocked."}
          </p>
        </div>

        {/* Days countdown if available */}
        {daysUntil !== null && daysUntil > 0 && (
          <div style={{ opacity: visible ? 1 : 0, transition: "all 0.6s ease 0.4s", marginBottom: 36 }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 10, background: "rgba(29,158,117,0.08)", border: "1px solid rgba(29,158,117,0.2)", borderRadius: 20, padding: "8px 20px", fontSize: 13, color: "#4DC9A0", fontWeight: 400 }}>
              <span style={{ fontFamily: "'Instrument Serif', serif", fontSize: 20, color: "#f0f4f2" }}>{daysUntil}</span>
              days until your first exam · make every one count
            </div>
          </div>
        )}

        {/* Features grid */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 10, marginBottom: 36, textAlign: "left" }}>
          {(plan === "elite" ? ELITE_FEATURES : PRO_FEATURES).map((f, i) => (
            <div key={f.label} className={`feature-item${featuresVisible ? " visible" : ""}`} style={{ transitionDelay: `${i * 80}ms`, padding: "14px 16px", background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 12, display: "flex", alignItems: "flex-start", gap: 12 }}>
              <div style={{ fontSize: 20, lineHeight: 1, marginTop: 1, flexShrink: 0 }}>{f.icon}</div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 500, color: "#f0f4f2", marginBottom: 2 }}>{f.label}</div>
                <div style={{ fontSize: 12, color: "rgba(240,244,242,0.35)", fontWeight: 300 }}>{f.sub}</div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div style={{ opacity: featuresVisible ? 1 : 0, transform: featuresVisible ? "translateY(0)" : "translateY(16px)", transition: "all 0.5s ease 0.6s" }}>
          <button className="continue-btn" onClick={onContinue}>
            Start studying →
          </button>
          <div style={{ marginTop: 14, fontSize: 12, color: "rgba(240,244,242,0.2)", fontWeight: 300 }}>
            Cancel any time · Your plan is active now
          </div>
        </div>

      </div>
    </div>
  );
}